import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { AsyncCache, fromEnv } from "../helpers";
import { IMoengageAnalyticPayload } from "../interfaces/Analytic.interface";
import { eventDispatcher } from "src/helpers/eventEmitter";
import { IEventParams } from "../interfaces/IEventParams";

export class MoengageService extends AsyncCache {
  static sendEvent(data: IMoengageAnalyticPayload) {
    return axios.post(
      `${fromEnv<string>("REACT_APP_HOST")}/analytics/moengage`,
      data
    );
  }
}

type Context = {
  sendEvent: (action: string, data?: object) => void;
  sendCustomEvent: (
    category: string,
    action: string,
    label?: string,
    data?: object
  ) => void;
};

type Props = {
  moengageEnabled: boolean;
  category: string;
  children?: React.ReactNode;
  eventParams?: IEventParams;
};

export const MoengageContext = createContext<Context>({
  sendEvent: () => {},
  sendCustomEvent: () => {},
});

export const useMoengage = () => useContext(MoengageContext);

export const MoengageProvider: React.FC<Props> = ({
  children,
  moengageEnabled = false,
  category = "",
  eventParams = {},
}) => {
  const [initiated, setInitiated] = useState(false);

  useEffect(() => {
    if (moengageEnabled) {
      setInitiated(true);
      const handleEvent = ({
        action,
        payload,
      }: {
        action: string;
        payload?: object;
      }) => {
        const mergedPayload = { ...eventParams, ...payload };
        const moengagePayload: IMoengageAnalyticPayload = {
          type: action,
          customer_id: mergedPayload.merchantId,
          actions: [
            {
              action,
              customer_id: mergedPayload.merchantId,
              current_time: new Date().toISOString(),
              attributes: {
                name: mergedPayload.fullName,
                email: mergedPayload.email,
              },
              platform: "web",
            },
          ],
        };
        MoengageService.sendEvent(moengagePayload);
      };
      eventDispatcher.subscribe(handleEvent);

      return () => {
        eventDispatcher.unsubscribe(handleEvent);
      };
    } else {
      console.warn("Moengage is disabled from config, so it will be disabled");
    }
  }, [moengageEnabled, eventParams]);

  if (!initiated) {
    return null;
  }

  const contextValue = {
    sendEvent: (action: string, data = {}): void => {
      eventDispatcher.publish({ action, payload: data });
    },
    sendCustomEvent: (
      customCategory: string,
      action: string,
      label = "",
      data = {}
    ): void => {
      eventDispatcher.publish({
        action,
        payload: { category: customCategory || category, label, ...data },
      });
    },
  };

  return (
    <MoengageContext.Provider value={contextValue}>
      {children}
    </MoengageContext.Provider>
  );
};
